// Обитатели на имот (наемодател / пропърти мениджър). Обитателят живее в
// имота, но НЕ е страна по наема — рядък случай (наемът се плаща от фирма
// или роднина). Добавя се по телефон: ако вече има акаунт-обитател, влиза
// веднага; иначе получава покана и се регистрира със SMS код.

import React, { useCallback, useState } from 'react';
import { Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { api } from '../../api';
import { confirmAction, showError, showSuccess } from '../../toast';
import { Button, Card, COLORS, Input, Label, Muted, Screen, SectionLabel, Title } from '../../ui';
import { getLocale, t } from '../../i18n';

type Resident = { id: string; userId: string; firstName: string; lastName: string; phone: string; addedAt: string };
type Pending = { id: string; phone: string; firstName: string; lastName: string; createdAt: string };

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG');

export default function ResidentsScreen({ route }: any) {
  const { apartmentId, address } = route.params ?? {};
  const [residents, setResidents] = useState<Resident[]>([]);
  const [pending, setPending] = useState<Pending[]>([]);
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);

  const apply = (r: { residents?: Resident[]; pending?: Pending[] }) => {
    setResidents(r.residents ?? []);
    setPending(r.pending ?? []);
  };

  const load = useCallback(() => {
    api(`/apartments/${apartmentId}/residents`).then(apply).catch(() => {});
  }, [apartmentId]);
  useFocusEffect(load);

  const add = async () => {
    setSaving(true);
    try {
      const r = await api(`/apartments/${apartmentId}/residents`, {
        body: { phone: phone.trim(), firstName: firstName.trim(), lastName: lastName.trim() },
      });
      apply(r);
      const registered = (r.residents ?? []).some((x: Resident) => x.phone === phone.trim());
      showSuccess(
        registered
          ? t('Обитателят е добавен към имота.')
          : t('Поканата е записана — човекът ще се регистрира със SMS код на този номер.')
      );
      setPhone('');
      setFirstName('');
      setLastName('');
    } catch (e: any) {
      showError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = (r: Resident) => {
    confirmAction(
      t('Премахване на обитател'),
      t('{name} ще загуби достъпа до адреса и нередностите на този имот.', { name: `${r.firstName} ${r.lastName}` }),
      t('Премахни'),
      async () => {
        try {
          await api(`/apartments/${apartmentId}/residents/${r.id}`, { method: 'DELETE' });
          showSuccess(t('Обитателят е премахнат.'));
          load();
        } catch (e: any) {
          showError(e.message);
        }
      }
    );
  };

  const cancelInvite = (p: Pending) => {
    confirmAction(
      t('Отмяна на поканата'),
      t('Поканата към {name} ({phone}) ще бъде отменена.', { name: `${p.firstName} ${p.lastName}`, phone: p.phone }),
      t('Отмени поканата'),
      async () => {
        try {
          await api(`/apartments/${apartmentId}/residents/invitations/${p.id}`, { method: 'DELETE' });
          showSuccess(t('Поканата е отменена.'));
          load();
        } catch (e: any) {
          showError(e.message);
        }
      }
    );
  };

  return (
    <Screen>
      <Title>{t('Обитатели')}</Title>
      {address ? <Muted>{address}</Muted> : null}
      <View style={{ height: 12 }} />

      {/* Обяснението стои винаги над формата — случаят е рядък и лесно се бърка с наемател */}
      <Card style={{ borderColor: COLORS.primary }}>
        <Text style={{ color: COLORS.text, fontWeight: '700', fontSize: 16, marginBottom: 6 }}>{t('Какво е обитател?')}</Text>
        <Muted>
          {t('Обитател е човек, който живее в имота, но НЕ е страна по наема — например когато наемът се плаща от фирма или роднина.')}
        </Muted>
        <View style={{ marginTop: 8, gap: 3 }}>
          <Muted>{t('✅ Вижда адреса на имота')}</Muted>
          <Muted>{t('✅ Подава нередности и ремонти и следи статуса им')}</Muted>
          <Muted>{t('❌ НЕ вижда наема, задълженията, плащанията, депозита, договора и фактурите')}</Muted>
          <Muted>{t('❌ Не може да плаща или да подписва')}</Muted>
        </View>
        <Muted style={{ marginTop: 8 }}>
          {t('Известията за нередности получавате вие и наемателят. Достъпът на обитателя спира автоматично при приключване на наемното отношение или когато го премахнете.')}
        </Muted>
      </Card>

      <Card>
        <Label>{t('Телефон')}</Label>
        <Input value={phone} onChangeText={setPhone} keyboardType="phone-pad" placeholder="+359 88 812 3456" />
        <Label>{t('Име')}</Label>
        <Input value={firstName} onChangeText={setFirstName} placeholder={t('Иван')} />
        <Label>{t('Фамилия')}</Label>
        <Input value={lastName} onChangeText={setLastName} placeholder={t('Иванов')} />
        <Button
          title={t('Добави обитател')}
          onPress={add}
          disabled={phone.trim().length < 6 || !firstName.trim() || !lastName.trim()}
          loading={saving}
        />
      </Card>

      <SectionLabel>{t('Обитатели')}</SectionLabel>
      {residents.length === 0 && (
        <Card>
          <Muted>{t('Няма добавени обитатели.')}</Muted>
        </Card>
      )}
      {residents.map((r) => (
        <Card key={r.id}>
          <Text style={{ fontSize: 16, fontWeight: '600', color: COLORS.text }}>
            {r.firstName} {r.lastName}
          </Text>
          <Muted>
            {r.phone} · {t('добавен на {date}', { date: fmtDate(r.addedAt) })}
          </Muted>
          <Button title={t('Премахни')} variant="ghost" onPress={() => remove(r)} />
        </Card>
      ))}

      {pending.length > 0 && (
        <>
          <SectionLabel>{t('Поканени, още нерегистрирани')}</SectionLabel>
          {pending.map((p) => (
            <Card key={p.id}>
              <Text style={{ fontSize: 16, fontWeight: '600', color: COLORS.text }}>{p.phone}</Text>
              <Muted>
                {p.firstName} {p.lastName} · {t('поканен на {date}', { date: fmtDate(p.createdAt) })}
              </Muted>
              <Button title={t('Отмени поканата')} variant="ghost" onPress={() => cancelInvite(p)} />
            </Card>
          ))}
        </>
      )}
    </Screen>
  );
}
